import { Table, Tag, Tooltip, Typography, Modal, Button } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
// import routes from "@/routes";
// import CustomLink from "@/components/ui/CustomLink";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  EyeOutlined,
} from "@ant-design/icons";
// import DownloadLink from "@/components/ui/DowloadLink";
import ChangeFieldView, {
  getChangeFieldRows,
} from "@/components/views/ChangeFieldView/ChangeFieldView";
import CollapsibleList from "@/components/ui/CollapsibleList";

const changeTypeMap = {
  created: { label: "Создание", color: "green", icon: <PlusOutlined /> },
  updated: { label: "Изменение", color: "default", icon: <EditOutlined /> },
  deleted: { label: "Удаление", color: "red", icon: <DeleteOutlined /> },
};

const entityTypeLabel = {
  task: "Задача",
  resource: "Ресурс",
  user: "Пользователь",
  employee: "Сотрудник",
  file: "Файл",
  folder: "Папка",
  unit: "Рабочая группа",
  object: "Объект",
  request: "Заявка",
  task_template: "Шаблон задачи",
};

const systemLogIgnoreKeys = ["created_at", "updated_at", "meta"];

const ChangedFieldsList = ({ record }) => {
  const items = getChangeFieldRows({
    before: record.before_change,
    after: record.after_change,
    onlyChanged: true,
    ignoreKeys: systemLogIgnoreKeys,
  }).map((row) => row.displayName);

  return items.length ? (
    <CollapsibleList items={items} count={2} width={220} />
  ) : (
    "—"
  );
};

function getEntitySnapshot(record) {
  return record?.after_change || record?.before_change || null;
}

// function getEntityHref(entityTypeCode, entityId, entity) {
//   const uuid = entityId || entity?.uuid || entity?.id;

//   switch (entityTypeCode) {
//     case "task":
//       return routes?.administration?.taskDetail?.(uuid) || "#";
//     case "resource":
//     case "object":
//       return routes?.administration?.objectDetail?.(entity?.definitionId, uuid) || "#";
//     case "user":
//       return routes?.administration?.userDetail?.(uuid) || "#";
//     case "employee":
//       return routes?.administration?.employeeDetail?.(uuid) || "#";
//     case "folder":
//       return routes?.administration?.folder?.(uuid) || "#";
//     case "unit":
//       return routes?.administration?.unitDetail?.(uuid) || "#";
//     case "request":
//       return routes?.administration?.taskDetail?.(uuid) || "#";
//     default:
//       return routes?.entity?.detail?.(uuid) || "#";
//   }
// }

function getEntityDisplayName(entityTypeCode, entity) {
  if (!entity) return "—";

  switch (entityTypeCode) {
    case "user":
      return entity.email || "—";

    case "employee":
      const parts = [
        entity.lastname,
        entity.firstname,
        entity.patronymic,
      ].filter(Boolean);
      return parts.join(" ") || "—";

    case "file":
      return entity.original_name;

    case "folder":
      return entity.name;

    case "task_template":
      return entity.template_title;

    default:
      return (
        entity.name ||
        entity.title ||
        entity.filename ||
        entity.login ||
        entity.email ||
        entity.path ||
        entity.code ||
        entity.id ||
        entity.uuid ||
        "—"
      );
  }
}

const SystemLogsTable = ({
  data,
  pagination,
  scroll = { x: "100%" },
  loading,
  onChange,
}) => {
  const [preview, setPreview] = useState(null); // record для модалки
  const openPreview = (record) => setPreview(record);
  const closePreview = () => setPreview(null);

  const columns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "change_type",
        key: "change_type",
        width: 48,
        render: (change_type) => {
          const cfg = changeTypeMap[change_type] || {
            label: change_type,
            color: "default",
          };
          return (
            <Tag
              color={cfg.color}
              icon={cfg.icon}
              title={cfg.label}
              aria-label={cfg.label}
            />
          );
        },
      },
      {
        title: "Сущность",
        key: "entity",
        ellipsis: { showTitle: true },
        render: (_, record) => {
          const entityTypeCode = record?.entity_type_code;
          // const entityId = record?.entity_id;
          const entity = getEntitySnapshot(record);

          const displayName = getEntityDisplayName(entityTypeCode, entity);
          // const href = getEntityHref(entityTypeCode, entityId, entity);

          const content = (
            <Tooltip placement="bottomLeft" title={displayName}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{
                  color: "inherit",
                  lineHeight: 1.4,
                  marginBottom: 0,
                  display: "-webkit-inline-box",
                }}
              >
                {displayName}
              </Typography.Paragraph>
            </Tooltip>
          );

          return (
            <>
              {/* {record?.change_type !== "deleted" ?
              <CustomLink href={href} color="#303030"> {content} </CustomLink>
            :  */}
              <div> {content} </div>
              {/* } */}
            </>
          );
        },
      },
      {
        title: "Тип сущности",
        dataIndex: "entity_type_code",
        key: "entity_type_code",
        width: 180,
        render: (type) => <Tag>{entityTypeLabel[type] || type}</Tag>,
      },
      {
        title: "Измененные поля",
        key: "changed_fields",
        width: 240,
        render: (_, record) => <ChangedFieldsList record={record} />,
      },
      {
        title: "Автор изменения",
        dataIndex: "actor",
        width: 300,
        key: "actor",
        render: (employee) =>
          employee ? <EmployeeProfile {...employee} /> : "—",
        ellipsis: { showTitle: true },
      },
      {
        title: "Дата/время изменения",
        dataIndex: "change_date",
        key: "change_date",
        width: 200,
        render: (val) => (val ? dayjs(val).format("DD.MM.YYYY HH:mm") : "—"),
      },
      {
        title: "",
        width: 80,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, record) => (
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openPreview(record)}
          ></Button>
        ),
      },
    ],
    []
  );

  // Рендер модалки сравнения
  const renderChangesFieldsModal = () => {
    if (!preview) return null;

    return (
      <Modal
        open={!!preview}
        onCancel={closePreview}
        onOk={closePreview}
        okText="Закрыть"
        cancelButtonProps={{ style: { display: "none" } }}
        title="Изменения сущности"
        width={1000}
      >
        <ChangeFieldView
          before={preview.before_change}
          after={preview.after_change}
          changeType={preview.change_type}
          ignoreKeys={systemLogIgnoreKeys}
        />
      </Modal>
    );
  };

  return (
    <>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        showSorterTooltip={false}
        scroll={scroll}
        rowClassName="top-align"
      />
      {renderChangesFieldsModal()}
    </>
  );
};

export default SystemLogsTable;
